import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Dacă tocmai ne întoarcem de la Google (avem session_id în URL),
    // sărim verificarea aici — se ocupă AuthCallback de schimbul de sesiune.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignorăm
    }
    setUser(null);
  };

  // Autentificare cu email + parolă.
  const login = async (email, password) => {
    const r = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(r.data);
    return r.data;
  };

  const register = async (email, password, name, groupId) => {
    const r = await axios.post(
      `${API}/auth/register`,
      { email, password, name, groupId: groupId || undefined },
      { withCredentials: true }
    );
    setUser(r.data);
    return r.data;
  };

  const forgotPassword = async (email) => {
    const r = await axios.post(
      `${API}/auth/forgot-password`,
      { email },
      { withCredentials: true }
    );
    return r.data;
  };

  const resetPassword = async (token, password) => {
    const r = await axios.post(`${API}/auth/reset-password`, { token, password });
    return r.data;
  };

  const resendVerification = async () => {
    const r = await axios.post(
      `${API}/auth/resend-verification`,
      {},
      { withCredentials: true }
    );
    return r.data;
  };

  const verifyEmail = async (token) => {
    const r = await axios.post(`${API}/auth/verify-email`, { token });
    return r.data;
  };

  const activatePremium = async () => {
    await axios.post(`${API}/premium/activate-test`, {}, { withCredentials: true });
    await checkAuth();
  };

  const deactivatePremium = async () => {
    await axios.post(`${API}/premium/deactivate-test`, {}, { withCredentials: true });
    await checkAuth();
  };

  // Pornește o plată reală (abonament Stripe) pentru un plan (lookup_key).
  const startCheckout = async (lookupKey) => {
    const r = await axios.post(
      `${API}/payments/checkout`,
      { lookup_key: lookupKey, origin_url: window.location.origin },
      { withCredentials: true }
    );
    return r.data;
  };

  const getPaymentStatus = async (sessionId) => {
    const r = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
    return r.data;
  };

  const cancelSubscription = async () => {
    const r = await axios.post(`${API}/payments/cancel-subscription`, {}, { withCredentials: true });
    await checkAuth();
    return r.data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        checkAuth,
        logout,
        login,
        register,
        forgotPassword,
        resetPassword,
        resendVerification,
        verifyEmail,
        isPremium: !!user?.isPremium,
        activatePremium,
        deactivatePremium,
        startCheckout,
        getPaymentStatus,
        cancelSubscription,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
